import CryptoJS from 'crypto-js';
import { getLoggedInUser, isUserLoggedIn, removeTokensInLocalStorage } from '@airtel/air-utils';
import { closeWebView, getContacts, getLoggedInNumber, isMobileApp, isNativeApp, isNewIOSApp, markFirebaseEvent } from '@airtel-lego/native-app-bridge';
import { isValidObject } from '@airtel/air-design-system';
import { generatePath, useLocation, useParams } from 'react-router-dom';
import { LOGIN_CONSTANTS, NP2P_PLANS_VARIANT, UTIL_CONSTANTS, getQueryString , CAMPAIGN_FOR_OTP_REMOVAL, VARIANT_CONSTANTS } from '../constants/AppConstants';
import { getBaseHref, isNewConnectionRoute } from './environment';
import { fireLogWithSentryBase } from '@airtel-lego/sentry';


export function useParamFinder(param: string) {
   const urlSearch = new URLSearchParams(useLocation().search);
   const params = useParams();
   return urlSearch.get(param) || params[param];
}

/**
 * check if local storage is supported or not
 * @returns {boolean}
 */
export const isSessionSupported = () => {
   const testKey = 'test',
      storage = window.sessionStorage;
   try {
      storage.removeItem(testKey);
      return true;
   } catch (error) {
      return false;
   }
};

function isIOSLike(): boolean {
   const ua = navigator.userAgent;
   const maxTouch = navigator.maxTouchPoints || 0;
   const iOSDevice = /iP(hone|od|ad)/i.test(ua);
   const iPadOSDesktopMode = /Macintosh/i.test(ua) && maxTouch > 1;
   return iOSDevice || iPadOSDesktopMode;
}

function smoothScrollToElement(el: HTMLElement, offset = 0, duration = 400) {
   const startY = window.pageYOffset;
   const targetY = el.getBoundingClientRect().top + startY - offset;
   const distance = targetY - startY;
   let startTime: number | null = null;

   function step(t: number) {
      if (startTime === null) startTime = t;
      const progress = Math.min((t - startTime) / duration, 1);
      const ease = progress < 0.5
         ? 2 * progress * progress
         : -1 + (4 - 2 * progress) * progress;
      window.scrollTo(0, startY + distance * ease);
      if (progress < 1) requestAnimationFrame(step);
   }

   requestAnimationFrame(step);
}

export const scrollingIntoView = (testid: string, footerOffset = 0) => {
  const elementToBeScrolledIntoView = document.querySelector(
    `[data-testid="${testid}"]`
  ) as HTMLElement | null;
  const topNavSection = document.querySelector(
    `[data-testid="common_add_details_page_TopNavSection"]`
  ) as HTMLElement | null;
 
  if (!elementToBeScrolledIntoView) return;

  if(topNavSection){
   smoothScrollToElement(elementToBeScrolledIntoView, 60, 500);
   return;
  }

  if (isIOSLike()) {
    // iOS/WebKit → manual scroll to avoid sticky footer disappearing
    smoothScrollToElement(elementToBeScrolledIntoView, footerOffset, 500);
  } else {
    // Other browsers → use native smooth
    elementToBeScrolledIntoView.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }
};


export const getFromSessionStorage = (key: string): any => {
   try {
      const item = window?.sessionStorage?.getItem(key);
      if (!item) return null;

      // Try to parse, if fails, return raw string
      try {
         return JSON.parse(item);
      } catch {
         return item; // plain string
      }
   } catch (error) {
      return null;
   }
};


export const sendmessage = (eventdata: { height: number }) => {
   try {
      window.parent.postMessage(eventdata, '*');
   } catch (error) {
      console.error('error in sendmessage');
   }
};

export const filterPlanList = (onUserAction: any, localData: any, getState: any) => {
   const { currentItem } = localData;
   const actions = [];

   actions.push({
      type: 'apiCall',
      payload: {
         header: {},
         requestType: 'POST',
         apiUrl: 'guardian/api/buy/postpaid/prepaid-to-postpaid/getAllPlans/v6',
         queryParams: {
            lob: 'POSTPAID',
            circle: 'ALL',
            salesChannel: 'SHOP',
            category: 'Retail',
            customerType: 'B2C',
            pagination: false,
            journey: 'P2P',
            isFilterClicked: currentItem?.isSelected,
         },
         body: {
            productBrandName: currentItem?.isSelected
               ? currentItem.value
               : 'Infinity,Infinity Family',
         },
      },
   });

   return onUserAction({ actions });
};

export const pseudoOnLoadAddDetails = (onUserAction: any, getState: any) => {
   const actions = [];
   const loggedInNumber = getState()?.storeValues?.user?.loggedInNumber;
   const step2Action = {
      type: 'nativeEventEmitter',
      payload: {
         eventName: 'AddDetailsStepWidget_Step2',
         eventData: {
            isExpanded: true,
            'breakpointProps.xl.formWidgetData.calloutProps': null,
            'breakpointProps.lg.formWidgetData.calloutProps': null,
            'breakpointProps.md.formWidgetData.calloutProps': null,
            'formWidgetData.calloutProps': null,
            'dynamicFieldsProps.msisdn.value': loggedInNumber,
            ...(!loggedInNumber && {
               'dynamicFieldsProps.msisdn.key': 'familyMobileNumber',
               'dynamicFieldsProps.msisdn.autoFocus': true,
            }),
         },
      },
   };

   actions.push(step2Action);
   actions.push({
      type: 'storeValuesInRedux',
      payload: {
         context: 'loggedInContext',
         data: {
            extraData14: loggedInNumber ? 'auth' : 'unauth',
         },
      },
   });

   return onUserAction({ actions });
};

export const handleHomeNavigation = (onUserAction: any, args: any) => {
   let url = 'https://www.airtel.in/';
   const actions = [];
   const { timeout = 0 } = args;

   if (isNativeApp({})) {
      setTimeout(() => {
         closeWebView();
      }, timeout);
   } else {
      actions.push({
         type: 'navigation',
         payload: { externalUrl: url, shouldAddSearchParams: true },
         actionWithDelayed: timeout,
      });
   }

   return onUserAction({
      actions: actions,
   });
};

export const selectPlanDetailsJourney = (onUserAction: any) => {
   const actions = [];

   actions.push({
      type: 'setDataInSessionStorage',
      target: 'session',
      payload: {
         key: 'journeyType',
         value: {
            feedUserType: 'postpaid-common',
            extraData15: 'common',
         },
         clearOnLogout: false,
      },
   });

   actions.push({
      payload: {
         requestType: 'POST',
         apiUrl: 'guardian/api/buy/postpaid/prepaid-to-postpaid/getPlanDetails/v6',
         queryParams: {
            customerType: 'B2C',
            pagination: false,
            journey: 'P2P',
            isCommon: true,
            price: '{{additionalData:queryParams.price}}',
            planId: '{{additionalData:queryParams.planId}}',
            circle: 'ALL',
            salesChannel: 'SHOP',
            category: 'Retail',
            lob: 'POSTPAID',
         },
         header: {},
         body: {
            productBrandName: 'Infinity,Infinity Family',
         },
      },
      type: 'apiCall',
   });

   return onUserAction({ actions });
};

export const selectPlanListingJourney = (onUserAction: any) => {
   const actions = [];
   // actions.push({
   //    type: 'storeValuesInRedux',
   //    payload: {
   //       context: 'journeyType',
   //       data: {
   //          journey: journey,
   //       },
   //       clearOnLogout: false,
   //    },
   // });

   actions.push({
      payload: {
         requestType: 'POST',
         apiUrl: 'guardian/api/buy/postpaid/prepaid-to-postpaid/getAllPlans/v6',
         queryParams: {
            customerType: 'B2C',
            pagination: false,
            journey: 'common',
            circle: 'ALL',
            salesChannel: 'SHOP',
            category: 'Retail',
            lob: 'POSTPAID',
         },
         header: {},
         body: {
            productBrandName: 'Infinity,Infinity Family',
         },
      },
      type: 'apiCall',
   });

   return onUserAction({ actions });
};

// create lead action
export const createLeadApiCall = (
   args: any,
   localData: any,
   onUserAction: any,
   getState: any
) => {
   const { storeValues } = getState();
   let apiVersion = (storeValues.versionContextNP2P?.version);
   const actions = [
      {
         payload: {
            requestType: 'POST',
            authTokenGroup: LOGIN_CONSTANTS.authKey,
            apiUrl: `guardian/api/buy/postpaid/non-postpaid/createCart/bundling/v${apiVersion ? apiVersion : '1'}`,
            queryParams: {
               customerType: 'B2C',
               pagination: false,
               journey: 'common',
               circle: 'ALL',
               salesChannel: 'SHOP',
               category: 'Retail',
               lob: 'POSTPAID',
            },
            header: {},
            body: {
               productBrandName: '',
            },
         },
         type: 'apiCall',
      },
   ];

   return onUserAction({ actions });
};

// set loggedIn user number to redux store
export const savedLoggedInSiNumberAndPrefillInput = (
   loggedInNumber: String,
   isUserLoggedIn: boolean = false,
   isWeb: boolean = false
) => {
   let journeyType
   // @ts-ignore
   const storeState = window.store?.getState()
   if (isSessionSupported()) {
      const intentSelected = getFromSessionStorage('intentSelected');
      journeyType = intentSelected?.type;
   }
   const dynamicFieldsProps: any = {
      msisdn: {
         value: loggedInNumber,
         readOnly: journeyType === "new-connection" || journeyType === "mnp",
         showLoader: true,
      },
   };
   const dynamicFieldsPropsForP2P: any = {
      msisdn: {
         value: loggedInNumber,
         readOnly: false,
         showLoader: true,
      },
   };

   if (isWeb) {
      dynamicFieldsProps.eid = {
         actionCta: {},
      };
      dynamicFieldsPropsForP2P.eid = {
         actionCta: {},
      };
   }
   const userAuthorised = isUserLoggedIn ? 'Auth' : 'Unauth';
   const actions: any = [];

   const sessionData: any = JSON.parse(
      sessionStorage.getItem('aa-postpaid-purchase') as any
   );

   if (sessionData?.eventCategory === 'coip_ingress_landing_page') return { actions };

   const isP2P = journeyType === 'p2p'

   if (isUserLoggedIn) {
      const landingLayoutName = getFromSessionStorage("ps_landingLayoutName")
      const isOtpShiftLayout = landingLayoutName == "common_bundling_otp_shift" || landingLayoutName == "p2p_bundling_otp_shift"
      const nameRemovalJourney = "name_removal" === storeState?.variantForRemovingName?.variant
      let eventCategory = isOtpShiftLayout ? "common_add_details_page" : "family_add_details_page"
      let section = isOtpShiftLayout ? "EnterMobileNumberSection" : "adddetailstepssection"
      let subSection = isOtpShiftLayout ? "EnterMobileNumberWidget" : "contact_number"
      actions.push(
         {
            type: 'storeValuesInRedux',
            payload: {
               context: 'user',
               data: {
                  loggedInNumber: loggedInNumber,
                  isUserLoggedIn: isUserLoggedIn,
               },
            },
         },
         {
            type: 'storeValuesInRedux',
            payload: {
               context: 'analyticsData',
               data: {
                  extraData15: userAuthorised,
               },
            },
         },
         {
            type: 'nativeEventEmitter',
            payload: {
               eventName: 'step1Widget',
               eventData: {
                  dynamicFieldsProps: dynamicFieldsProps,
               },
            },
         },
         {
            type: 'nativeEventEmitter',
            payload: {
               eventName: 'EnterMobileNumberWidget',
               eventData: {
                  dynamicFieldsProps: dynamicFieldsPropsForP2P,
               },
            },
         },
         {
            analyticsAction: 'click',
            analyticsData: {
               eVar21: loggedInNumber,
               eventCategory: isP2P ? eventCategory : 'common_add_details_page',
               section: isP2P ? section : 'nonp2psection',
               subSection: isP2P ? subSection : 'lead_contact_details_step1',
               eventLabel: 'mobilenumber',
               eventAction: 'click',
               prop26: 'auto',
               prop45: journeyType || "",
               ...(
                  isP2P && isOtpShiftLayout && {
                     eVar0: "p2p_safo p2p mobile fill"
                  }
               ),
            },
         },!isP2P && nameRemovalJourney && {
                 type: 'callUtilFunction',
                 payload: {
                    args: [['operationType', 'name']],
                    utilName: 'handleBundledQL',
                 },
              },
              !isP2P && nameRemovalJourney && {
                 type: 'nativeEventEmitter',
                 payload: {
                    eventData: {
                       isLoading: true,
                    },
                    eventName: 'step1Widget',
                 },
              }
      );
   } else {
      actions.push({
         type: 'storeValuesInRedux',
         payload: {
            context: 'analyticsData',
            data: {
               extraData15: userAuthorised,
            },
         },
      });
   }

   return { actions };
};

// use to set initial analytics values
export const updateAnalyticsData = (args: any, getState: any, onUserAction: any) => {
   const { storeValues } = getState();
   const { type } = args;
   const { storeConnectionIntent } = storeValues;
   const actions = [];

   if (type && Object.keys(storeConnectionIntent).length > 0) {
      actions.push({
         payload: {
            key: 'connectionIntentType',
            value: {
               type: storeConnectionIntent?.[type] || '',
            },
         },
         target: 'session',
         type: 'setDataInSessionStorage',
      });
   }

   return onUserAction({ actions });
};

// handle redirection on omni widget address redirection
export const handleRedirectionOnOmniWidgetAddress = (
   args: any,
   onUserAction: any,
   getState: any
) => {
   const { storeValues } = getState();
   const actions = [];
   let search = window.location.search;
   let params = new URLSearchParams(search);
   let lob = params.get('lob');
   let subheading = '';

   if (lob === 'BROADBAND') subheading = 'on your new Fiber connection';
   else if (lob === 'DTH') subheading = 'on your new DTH connection';
   // if circleId not in stored address, then redirect to landing page.
   if (!storeValues?.addressData?.circleId) {
      actions.push({
         payload: {
            type: 'goback',
            goBackDelta: 2,
            screenName: '/add_details',
            externalUrl: '',
            delay: 0,
            shouldScrollToTop: false,
            params: {},
         },
         type: 'navigation',
      });
   } else {
      // if (+packInfo?.amount > 0) {
      // show grafitti on omni widget address
      actions.push(
         {
            payload: {
               context: 'analyticsData',
               data: {
                  offerTitle: 'You will save ₹ 100/month',
               },
            },
            type: 'storeValuesInRedux',
         }
         // {
         //    payload: {
         //       widgetId: 'AirOfferPopupWidgetRecom',
         //       widgetData: {
         //          title: 'Offer unlocked!',
         //          imageUrl:
         //             'https://assets.airtel.in/static-assets/airtel-pay-sdk/offer.png',
         //          offerHeading: 'You will save ₹ 100/month',
         //          offerSubHeading: subheading,
         //          dismissDuration: 3000,
         //          showLottie: true,
         //          lottieProps: {
         //             uri: 'https://assets.airtel.in/static-assets/airtel-pay-sdk/ps-lottie-congratulation.json',
         //             loop: true,
         //          },
         //       },
         //    },
         //    type: 'updateWidgetProps',
         // },
         // {
         //    analyticsAction: 'impression',
         // }
      );
      // }
   }

   return onUserAction({ actions });
};

const setMobileNumberOnValid = (actionContextData: any) => {
   return {
      type: 'storeValuesInRedux',
      payload: {
         context: 'userData',
         data: {
            userEnteredValue: actionContextData?.value,
         },
      },
   };
};

const getRandomUUID = () => {
   let dt = new Date().getTime();
   const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = (dt + Math.random() * 16) % 16 | 0;
      dt = Math.floor(dt / 16);
      return (c == 'x' ? r : (r & 0x3) | 0x8).toString(16);
   });

   return uuid;
};

// fetch user name and prefill to form
const fetchUserName = (storeValues: any) => {
   let apiVersion = (storeValues.versionContextNP2P?.version);
   let rtn = storeValues?.userData?.rtn;
   const isCoipUser = !!storeValues?.userData?.isCoipUser;
   const layout = storeValues?.variantContext?.variant
   let apiUrl = `guardian/api/buy/postpaid/non-postpaid/getName/bundling/v${isCoipUser ? '20' : (apiVersion ? apiVersion : '1')}`;

   if (layout == 'bundling_one_airtel' || layout == 'p2p_at_end_one_airtel' || layout === VARIANT_CONSTANTS.BUNDLING_PRIORITY_POSTPAID) {
      apiUrl = `guardian/api/buy/postpaid/non-postpaid/getName/elite/v2`
   }

   return {
      payload: {
         authTokenGroup: LOGIN_CONSTANTS.authKey,
         header: {
            'x-request-id': getRandomUUID(),
         },
         queryParams: {
            type: 'SI',
            lineOfBusiness: 'MOBILITY',
            isHardBundle: storeValues?.packInfo?.bundleType,
            isCoipUser,
            rtn,
            layout,
            ...(apiVersion == '2' && { siNumber: storeValues?.userData?.rtn })
         },
         requestType: 'GET',
         apiUrl,
      },
      type: 'apiCall',
   };
};

const fireGtmDataLayerEvent = (stepName: String, eventName: String, clickText: String = "") => {
   return {
      payload: {
         args: [
            {
               stepName: stepName,
               clicktext: clickText,
               event: eventName,
               payment_Frequency: 'monthly'
            }
         ],
         utilName: 'handleGtmDataLayerEvent'
      },
      type: 'callUtilFunction'
   }
}




export const checkDomain = (onUserAction: any, getState: any) => {
   const actions: any = [];
   const { storeValues } = getState();
   const blackListedDomains = storeValues.emailValidation?.domainList || [];
   const email = storeValues.userData?.email;
   const domain = email.split('@')[1];

   const isBlackListed = blackListedDomains.includes(domain.toLowerCase());

   if (isBlackListed) {
      actions.push(
         {
            analyticsAction: 'click',
            analyticsData: {
               eventCategory: 'COIP_INGRESS_LANDING_PAGE',
               extraData11: '{{sessionStorage:ps_landingLayoutName}}',
               section: 'emailsection',
               subSection: 'emailwidget',
               eventAction: 'error',
               eventLabel: 'verify via otp',
               prop14: '{{storeValues:userData.email}}',
               eVar33: 'Please enter your official corporate email id',
               appNumber: '-1',
            },
         },
         {
            payload: {
               eventData: {
                  dynamicFieldsProps: {
                     emailAddress: {
                        hintText: 'Please enter your official corporate email id',
                        state: 'error',
                     },
                  },
               },
               eventName: 'EmailWidget',
            },
            type: 'nativeEventEmitter',
         }
      );
   } else {
      actions.push(
         {
            payload: {
               otpDigitCount: 4,
               otpWidgetProps: {
                  onChangeCtaPress: {
                     id: 'onMsisdnChange',
                     actions: [
                        {
                           analyticsAction: 'click',
                           analyticsData: {
                              eventCategory: 'COIP_INGRESS_LANDING_PAGE',
                              extraData11: '{{sessionStorage:ps_landingLayoutName}}',
                              section: 'otpModalSection',
                              subSection: 'otpModalWidget',
                              eventAction: 'click',
                              eventLabel: 'change email',
                              prop14: '{{storeValues:userData.email}}',
                           },
                        },
                     ],
                  },
                  changeNumberCtaAccessibilityProps: {
                     accessibilityLabel: 'Change email address',
                     accessible: true,
                  },
                  infoTextTop: 'Enter OTP',
                  onOtpBoxPress: {
                     actions: [
                        {
                           analyticsAction: 'click',
                           analyticsData: {
                              eventCategory: 'COIP_INGRESS_LANDING_PAGE',
                              extraData11: '{{sessionStorage:ps_landingLayoutName}}',
                              section: 'otpModalSection',
                              subSection: 'otpModalWidget',
                              eventAction: 'click',
                              eventLabel: 'otp',
                              prop14: '{{storeValues:userData.email}}',
                           },
                        },
                     ],
                  },
                  onInputFilled: {
                     actions: [
                        {
                           analyticsAction: 'click',
                           analyticsData: {
                              eventValue: 'otp filled',
                              eventCategory: 'COIP_INGRESS_LANDING_PAGE',
                              extraData11: '{{sessionStorage:ps_landingLayoutName}}',
                              section: 'otpModalSection',
                              subSection: 'otpModalWidget',
                              eventAction: 'click',
                              eventLabel: 'otp',
                              prop14: '{{storeValues:userData.email}}',
                           },
                        },
                     ],
                  },
                  title: 'Verify email address',
                  rtnOnChangeCta: true,
                  primaryCtaProps: {
                     onPress: {
                        id: 'otpVerifySubmit',
                        actions: [
                           {
                              analyticsAction: 'click',
                              analyticsData: {
                                 eventCategory: 'COIP_INGRESS_LANDING_PAGE',
                                 extraData11: '{{sessionStorage:ps_landingLayoutName}}',
                                 section: 'otpModalSection',
                                 subSection: 'otpModalWidget',
                                 eventAction: 'click',
                                 eventLabel: 'verify otp',
                                 prop14: '{{storeValues:userData.email}}',
                              },
                           },
                        ],
                     },
                     buttonType: 'primary',
                     style: {
                        marginTop: 0,
                     },
                     title: 'verify',
                  },
                  secondaryCtaProps: {
                     onPress: {
                        counterSuffix: 'S',
                        id: 'otpResendClick',
                        title: 'RESEND',
                        actions: [
                           {
                              analyticsAction: 'click',
                              analyticsData: {
                                 eventCategory: 'COIP_INGRESS_LANDING_PAGE',
                                 extraData11: '{{sessionStorage:ps_landingLayoutName}}',
                                 section: 'otpModalSection',
                                 subSection: 'otpModalWidget',
                                 eventAction: 'click',
                                 eventLabel: 'resend otp',
                                 prop14: '{{storeValues:userData.email}}',
                              },
                           },
                        ],
                     },
                  },
               },
               onSendOtpFailure: {
                  actions: [],
               },
               groupId: 'COIP_EMAIL_FLOW',
               modalProps: {
                  showCross: true,
                  withBreakpoint: true,
                  onOpenMetaData: {
                     actions: [
                        {
                           analyticsAction: 'impression',
                           analyticsData: {
                              eventCategory: 'COIP_INGRESS_LANDING_PAGE',
                              extraData11: '{{sessionStorage:ps_landingLayoutName}}',
                              section: 'otpModalSection',
                              subSection: 'otpModalWidget',
                              eventAction: 'pageloaded',
                              prop14: '{{storeValues:userData.email}}',
                           },
                        },
                     ],
                  },
                  withBreakpointProps: {
                     lg: {
                        containerStyle: {
                           maxWidth: '392px',
                        },
                     },
                  },
               },
               sessionStorageKey: 'COIP_EMAIL_FLOW',
               msisdn: '{{storeValues:userData.email}}',
               email: '{{storeValues:userData.email}}',
               onVerifyOtpSuccess: {
                  actions: [
                     {
                        payload: {
                           authTokenGroup: 'COIP_EMAIL_FLOW',
                           requestType: 'POST',
                           apiUrl: 'guardian/api/buy/postpaid/onLoad/v1',
                           queryParams: {
                              isCoipUser: true,
                           },
                           header: {},
                           body: {
                              operationType: 'emailCheckEligibility',
                           },
                        },
                        type: 'apiCall',
                     },
                  ],
               },
            },
            type: 'otpConsent',
         },
         {
            payload: {
               value: {},
               key: 'COIP_EMAIL_FLOW',
            },
            type: 'setDataInSessionStorage',
            target: 'session',
         },
         {
            analyticsAction: 'click',
            analyticsData: {
               eventCategory: 'COIP_INGRESS_LANDING_PAGE',
               extraData11: '{{sessionStorage:ps_landingLayoutName}}',
               section: 'emailsection',
               subSection: 'emailwidget',
               eventAction: 'click',
               eventLabel: 'verify via otp',
               prop14: '{{storeValues:userData.email}}',
            },
         }
      );
   }

   return onUserAction({
      actions,
   });
};

const enableNamefield = (value: boolean = false, delayedValue = 0) => {
   const obj: any = { isDisabled: value };
   if (value) obj['value'] = ''; // this will set input value as empty

   const data: any = {
      type: 'nativeEventEmitter',
      payload: {
         eventName: 'step1Widget',
         eventData: {
            id: 'step1Widget',
            dynamicFieldsProps: { name: obj },
         },
      },
   };

   if (delayedValue > 0) {
      data['actionWithDelayed'] = delayedValue;
   }

   return data;
};

const actionsForBundledQLPostOtpVerify = (delayedValue : number = 0) => {
   const data: any = {
      type: 'callUtilFunction',
      payload: {
         "args": [
            [
               "operationType",
               "name"
            ]
         ],
         "utilName": "handleBundledQL"
      },
   };
   if (delayedValue > 0) {
      data['actionWithDelayed'] = delayedValue;
   }
   return data
}
const actionsForStep1WidgetPostOtpVerify = (delayedValue : number = 0) => {
   const data: any = {
      type: 'nativeEventEmitter',
      payload: {
         "eventData": {
            "isLoading": true
         },
         "eventName": "step1Widget"
      },
   };
   if (delayedValue > 0) {
      data['actionWithDelayed'] = delayedValue;
   }
   return data
}

export const removeTokensFromSessionStorage = (key: string) => {
   try {
      sessionStorage.removeItem(key);
   } catch (error) {
      console.error('error: removeTokensFromSessionStorage: ', error);
   }
};

// add otp consent action
export const otpConsentAction = (
   args: any,
   getState: any,
   onUserAction: any,
   actionContextData: any
) => {
   const { storeValues } = getState();
   const { user } = storeValues;
   const actions: any[] = [];
   let isNumberChanged = false;
   const isApp = isMobileApp();
   let otpFlowDisable = false;
   const urlParams = new URLSearchParams(window.location.search);
   const utmSource = urlParams.get('utm_source') || ''

   // to remove otp from list of utm_sources
   otpFlowDisable = CAMPAIGN_FOR_OTP_REMOVAL?.[utmSource]

   const variant = storeValues?.variantName?.variant ?? ""
   const nameRemovalJourney = "name_removal" === storeValues?.variantForRemovingName?.variant
   const userEnteredNumber = actionContextData?.value || args?.msisdn || null;
   const variantContext = storeValues?.variantContext?.variant ?? ""
   const isPriorityPostpaidVariant = variantContext === VARIANT_CONSTANTS.PRIORITY_POSTPAID || variantContext === VARIANT_CONSTANTS.BUNDLING_PRIORITY_POSTPAID
   const groupId = isPriorityPostpaidVariant ? LOGIN_CONSTANTS.priorityPostpaidGroupId : LOGIN_CONSTANTS.groupId

   removeTokensFromSessionStorage(LOGIN_CONSTANTS.authKey);

   const checkForOTPFlow = (finalNumber: string) => {
      // if entered number and loggedIn number are not same then ask for OTP
      let isEnteredNumberSameWithLoggedInNumber = userEnteredNumber === finalNumber
      if (!isEnteredNumberSameWithLoggedInNumber && !otpFlowDisable) {
         isNumberChanged = true;
         // remove old tokens
         removeTokensInLocalStorage();

         let otpVerifyActionsList: any = nameRemovalJourney ? [fetchUserName(storeValues), actionsForBundledQLPostOtpVerify(), actionsForStep1WidgetPostOtpVerify()] : [fetchUserName(storeValues), enableNamefield()];
         let otpResendActionsList: any = [
            {
               analyticsAction: 'click',
               analyticsData: {
                  id: 'otpResendClick',
                  eventCategory: 'common_add_details_page',
                  section: 'OtpModalSectionFamily',
                  subSection: 'AirOtpWidget',
                  eventLabel: 'resend',
                  eventAction: 'click',
                  extraData2: '{{storeValues:userData.lob}}',
                  extraData7: '{{sessionStorage:connectionIntentType.type}}',
                  extraData11: '{{sessionStorage:ps_landingLayoutName}}',
                  prop8: '{{sessionStorage:journeyType.feedUserType}}',
                  eVar21: '{{storeValues:userData.rtn}}',
               },
            },
         ];

         if (variant === NP2P_PLANS_VARIANT) {
            otpVerifyActionsList.push(
               fireGtmDataLayerEvent("2|Contact Details", "lead_otp_submit")
            );
            otpResendActionsList.push(
               fireGtmDataLayerEvent("2|Contact Details", "otp_resend")
            );
         }

         actions.push(
            {
               payload: {
                  otpDigitCount: 4,
                  onSendOtpFailure: { actions: [] },
                  groupId: groupId,
                  sessionStorageKey: LOGIN_CONSTANTS.authKey,
                  msisdn: userEnteredNumber,
                  onVerifyOtpSuccess: { actions: otpVerifyActionsList },
                  otpWidgetProps: {
                     title: 'Verify Mobile Number',
                     titleProps: { tagName: "h1" },
                     hintTextAccessibilityProps: { tabIndex: 0 },
                     infoTextTop: isPriorityPostpaidVariant? '' : 'enter OTP',
                     onChangeCtaPress: {
                        id: 'onMsisdnChange',
                        actions: [
                           {
                              payload: {
                                 eventData: {
                                    dynamicFieldsProps: { msisdn: { showLoader: false } },
                                 },
                                 shouldResolveData: true,
                                 eventName: 'step1Widget',
                              },
                              type: 'nativeEventEmitter',
                           },
                           {
                              analyticsAction: 'click',
                              analyticsData: {
                                 eventCategory: 'common_add_details_page',
                                 section: 'OtpModalSectionFamily',
                                 subSection: 'AirOtpWidget',
                                 eventLabel: 'change',
                                 eventAction: 'click',
                                 extraData2: '{{storeValues:userData.lob}}',
                                 extraData7: '{{sessionStorage:connectionIntentType.type}}',
                                 extraData11: '{{sessionStorage:ps_landingLayoutName}}',
                                 prop8: '{{sessionStorage:journeyType.feedUserType}}',
                                 eVar21: '{{storeValues:userData.rtn}}',
                              },
                           },
                        ],
                     },
                     onInputFilled: {
                        actions: [
                           {
                              analyticsAction: 'click',
                              analyticsData: {
                                 eVar0: variant === NP2P_PLANS_VARIANT ? 'np2p_otp filled' : '',
                                 eventValue: 'otp filled',
                                 eventCategory: 'common_add_details_page',
                                 section: 'OtpModalSectionFamily',
                                 subSection: 'AirOtpWidget',
                                 eventLabel: 'otp',
                                 eventAction: 'click',
                                 prop26: 'manual',
                                 extraData2: '{{storeValues:userData.lob}}',
                                 extraData7: '{{sessionStorage:connectionIntentType.type}}',
                                 extraData11: '{{sessionStorage:ps_landingLayoutName}}',
                                 prop8: '{{sessionStorage:journeyType.feedUserType}}',
                                 eVar21: '{{storeValues:userData.rtn}}',
                                 customLabel: variant === NP2P_PLANS_VARIANT ? 'button' : '',
                                 cartId: '{{storeValues:orderData.orderData.cartItemId}}',
                                 ticketId: '{{storeValues:orderData.orderData.cartId}}',
                                 eVar20: '{{storeValues:user.loggedInNumber}}',
                                 prop5: '{{storeValues:packInfo.price}}',
                                 prop10: 'otp journey',
                                 eVar25: '{{storeValues:orderData.address.circleId}}',
                                 prop45: "{{storeValues:pageIntent.pageIntentValue}}"
                              },
                           },
                        ],
                     },
                     primaryCtaProps: {
                        onPress: {
                           id: 'otpVerifySubmit',
                           actions: [
                              {
                                 analyticsAction: 'click',
                                 analyticsData: {
                                    eVar0: variant === NP2P_PLANS_VARIANT ? 'np2p_otp verify click' : '',
                                    id: 'otpVerifySubmit',
                                    eventCategory: 'common_add_details_page',
                                    section: 'OtpModalSectionFamily',
                                    subSection: 'AirOtpWidget',
                                    eventLabel: 'verify',
                                    eventAction: 'click',
                                    extraData2: '{{storeValues:userData.lob}}',
                                    extraData7:
                                       '{{sessionStorage:connectionIntentType.type}}',
                                    extraData11:
                                       '{{sessionStorage:ps_landingLayoutName}}',
                                    prop8: '{{sessionStorage:journeyType.feedUserType}}',
                                    eVar21: '{{storeValues:userData.rtn}}',
                                    customLabel: variant === NP2P_PLANS_VARIANT ? 'button' : '',
                                    cartId: '{{storeValues:orderData.orderData.cartItemId}}',
                                    ticketId: '{{storeValues:orderData.orderData.cartId}}',
                                    eVar20: '{{storeValues:user.loggedInNumber}}',
                                    prop5: '{{storeValues:packInfo.price}}',
                                    prop10: 'otp journey',
                                    eVar25: '{{storeValues:orderData.address.circleId}}',
                                    prop45: "{{storeValues:pageIntent.pageIntentValue}}"
                                 },
                              },
                           ],
                        },
                        buttonType: 'primary',
                        style: { marginTop: 0 },
                        title: 'verify',
                     },
                     secondaryCtaProps: {
                        onPress: {
                           title: 'RESEND',
                           counterSuffix: 'S',
                           id: 'otpResendClick',
                           actions: otpResendActionsList,
                        },
                     },
                     buttonWrapperStyle: { marginTop: 0 },
                  },
               },
               type: 'otpConsent',
            },
            {
               type: 'storeValuesInRedux',
               payload: {
                  context: 'otpEnabled',
                  data: { isOtpEnabled: "OTP Journey" },
               },
            }
         );

         if (variant === NP2P_PLANS_VARIANT) {
            actions.push(fireGtmDataLayerEvent("2|Contact Details", "otp_verification_viewed"));
            actions.push({
               analyticsAction: 'impression',
               analyticsData: {
                  eVar0: 'np2p_otp impression',
                  eventCategory: 'common_add_details_page',
                  section: 'nonp2psection',
                  subSection: 'lead_contact_details_step2',
                  eventAction: 'otp-score-impression',
                  eVar21: '{{storeValues:userData.rtn}}',
                  customLabel: variant === NP2P_PLANS_VARIANT ? 'card' : '',
                  cartId: '{{storeValues:orderData.orderData.cartItemId}}',
                  ticketId: '{{storeValues:orderData.orderData.cartId}}',
                  eVar20: '{{storeValues:user.loggedInNumber}}',
                  prop5: '{{storeValues:packInfo.price}}',
                  prop10: 'otp journey',
                  eVar25: '{{storeValues:orderData.address.circleId}}',
                  prop45: "{{storeValues:pageIntent.pageIntentValue}}"
               },
            })
         }
      }

      //if web unauth , it will not go into this code
      // if web auth and number changed // it will not go
      //if web auth and number not changed // it will go into this

      // to clear name field
      if(nameRemovalJourney){
         if(isEnteredNumberSameWithLoggedInNumber || otpFlowDisable){
         actions.push(actionsForBundledQLPostOtpVerify(1500));
         actions.push(actionsForStep1WidgetPostOtpVerify(1500));
         }  
   }else{
         actions.push(enableNamefield(isNumberChanged, 100));
      }
      actions.push(setMobileNumberOnValid(actionContextData));

      if (isApp && isEnteredNumberSameWithLoggedInNumber) {
         actions.push(fetchUserName(storeValues));
      } else {
         actions.push(
            {
               analyticsAction: 'impression',
               analyticsData: {
                  eventCategory: 'common_add_details_page',
                  subSection: 'lead_contact_details_step1',
                  eventLabel: 'bundling eligible',
                  eventValue: 'no',
                  eventAction: 'impression',
                  extraData2: '{{storeValues:userData.lob}}',
                  extraData7: '{{sessionStorage:connectionIntentType.type}}',
                  extraData11: '{{sessionStorage:ps_landingLayoutName}}',
                  prop8: '{{sessionStorage:journeyType.feedUserType}}',
                  extraData14: 'Unauth',
                  extraData15: '{{storeValues:analyticsData.extraData15}}',
                  eVar26: 'postpaid-purchase',
               },
            },
            {
               type: 'storeValuesInRedux',
               payload: {
                  context: 'analyticsData',
                  data: { extraData14: 'Unauth' },
               },
            }
         );
      }

      return onUserAction({ actions });
   };

   let loggedInNumber = user?.loggedInNumber;
   // 🔹 If number already available — continue directly
   if (loggedInNumber) {
      checkForOTPFlow(loggedInNumber);
   } else {
      // 🔹 Otherwise, fetch it first (async)
      getLoggedInUserNumber()
         .then((number) => checkForOTPFlow(number))
         .catch((err) => {
            fireLogWithSentryBase(
               "otpConsentAction → failed to fetch loggedInNumber",
               err
            );
            checkForOTPFlow(""); // fallback to empty number
         });
   }
};

// show offer card and Graffiti
export const showOfferCard = (
   args: any,
   actionContextData: any,
   onUserAction: any,
   getState: any
) => {
   const { storeValues } = getState();
   const { isHardBundle } = storeValues?.userData;
   let amount = 100;
   const actions = [];

   if (isHardBundle) {
      actions.push({
         id: 'Bundling_OfferPopupWidget',
         payload: {
            eventName: 'Bundling_OfferPopupWIdget',
            eventData: {
               imageUrl:
                  'https://assets.airtel.in/static-assets/airtel-pay-sdk/offers/xxhdpi/congratulations.png',
               offerHeading: `${amount} off`,
               offerSubHeading: 'on your HD box',
               offerTitle: 'congratulations',
               subTitle: "You've saved",
               testID: 'OfferPopupWidgetContainer',
               title: 'Yay, offer applied!',
            },
         },
         type: 'nativeEventEmitter',
      });
   }

   return onUserAction({ actions });
};

export const checkValidDetails = (onUserAction: any, getState: any) => {
   const { storeValues } = getState();
   const isValidContact = storeValues?.stepsInfo?.isValidContact;
   const isValidAddress = storeValues?.stepsInfo?.isValidAddress;
   const isValidPlan = storeValues?.stepsInfo?.isValidPlan;

   let journey = 'addOn';
   if (isSessionSupported()) {
      const journeyType = getFromSessionStorage('journeyType');
      journey = journeyType?.journey;
   }

   const actionArr = [];

   const isValid =
      journey == 'addOn'
         ? isValidContact && isValidAddress
         : isValidContact && isValidAddress && isValidPlan;

   actionArr.push({
      type: 'nativeEventEmitter',
      payload: {
         shouldResolveData: false,
         eventName:
            journey == 'addOn' ? 'ConsentAnchorWidget_Child' : 'ConsentAnchorWidget',
         eventData: {
            isValid: isValid,
         },
      },
   });

   return onUserAction({
      actions: actionArr,
   });
};

// update Security deposit and plan id in redux on step 4 plan select click
const checkEligibilityOnPlanChange = (storeValues: any, planId: any) => {
   const version = storeValues?.versionContext?.version ?? 'v9';
   const updateBody = { ...storeValues.eligibilityBody.requestBody };

   if (isValidObject(updateBody)) {
      if (
         updateBody?.productOfferingQualificationItem?.[0]?.product
            ?.productOfferingQualificationItem?.[0]?.product?.productOffering?.id
      ) {
         updateBody.productOfferingQualificationItem[0].product.productOfferingQualificationItem[0].product.productOffering.id =
            planId;
      }

      if (
         updateBody?.productOfferingQualificationItem?.[0]?.product
            ?.productOfferingQualificationItem?.[0]?.product?.productPrice?.[0]?.price
            ?.taxIncludedAmount?.value
      ) {
         updateBody.productOfferingQualificationItem[0].product.productOfferingQualificationItem[0].product.productPrice[0].price.taxIncludedAmount.value =
            storeValues?.selectedPlanContext?.securityDeposit?.price?.value;
      }

      if (updateBody?.securityDeposit) {
         updateBody.securityDeposit =
            '{{storeValues:selectedPlanContext.securityDeposit}}';
      }

      return {
         type: 'apiCall',
         payload: {
            authTokenGroup: 'FAMILY_PARENT',
            requestType: 'POST',
            apiUrl:
               'guardian/api/buy/postpaid/prepaid-to-postpaid/eligibility/' + version,
            queryParams: {
               journey: '{{sessionStorage:journeyType.journey}}',
            },
            transactUserGroup: null,
            body: updateBody,
         },
      };
   }
};

// function to update query params without page refresh
const updateQueryParamsHelper = (newParams: any = {}) => {
   if (isValidObject(newParams)) {
      const currUrl = window?.location.pathname;
      const searchParams = new URLSearchParams(window.location.search);

      Object.entries(newParams).forEach(([key, value]: [string, any]) => {
         searchParams.set(key, value);
      });

      const newPath = `${currUrl}?${searchParams}`;
      window?.history.pushState(null, '', newPath);
   }
};

// TODO: to be handled at artist level later
export const updatePlanChange = (onUserAction: any, getState: any) => {
   const { storeValues } = getState();
   const planId = storeValues?.packInfo?.planId;
   const price = storeValues?.packInfo?.price;
   const isPaymentRequired = storeValues?.paymentContext?.isPaymentRequired;
   const paymentAmount = storeValues?.paymentContext?.paymentAmount;
   const { isLeadFlow } = getFromSessionStorage('family_lead_flow') || {};
   const actions = [];
   const newParams = {
      price,
      planId,
   };

   updateQueryParamsHelper(newParams);

   if (isPaymentRequired) {
      actions.push({
         type: 'nativeEventEmitter',
         payload: {
            eventName: 'AddDetailsStepWidget_Step4',
            eventData: {
               'formWidgetData.calloutProps': {
                  description:
                     'Security deposit of ₹' +
                     paymentAmount +
                     ' applicable on your pin code. This amount is fully refundable.',
                  showCrossDismissBtn: false,
                  iconName: 'InfoIcon',
                  state: 'lilac',
                  version: 'variable-width',
                  style: {
                     marginHorizontal: 20,
                     marginTop: 8,
                     marginBottom: 16,
                  },
               },
            },
         },
      });
   }

   const eligibilityApiCall = checkEligibilityOnPlanChange(storeValues, planId);
   if (isValidObject(eligibilityApiCall) && !isLeadFlow) {
      actions.push({
         type: 'storeValuesInRedux',
         payload: {
            context: 'stepsInfo',
            data: {
               isValidPlan: false,
            },
         },
      });
      actions.push({
         type: 'callUtilFunction',
         payload: {
            utilName: 'checkValidDetails',
            args: [],
         },
      });
      actions.push(eligibilityApiCall);
   }

   return onUserAction({
      actions: actions,
   });
};

export const setJourneyTypeInSessionStorage = (onUserAction: any) => {
   if (!isSessionSupported()) return;

   const value: any = {
      journey: 'addOn',
      extraData15: 'common',
      feedUserType: 'postpaid-common',
   };

   sessionStorage.removeItem('intentSelected');
   sessionStorage.removeItem('bundlingThanksPageData');

   const actions = [
      {
         payload: {
            clearOnLogout: false,
            value,
            key: 'journeyType',
         },
         type: 'setDataInSessionStorage',
         target: 'session',
      },
   ];

   return onUserAction({
      actions,
   });
};

export const callFetchManageDetails = ({ args, onUserAction, getState }: any) => {
   const { siNumber, name, planId, journey, channel, ingress, siNumberManage } = getQueryString();
   const simCardType = args[0]

   const actions = [
      {
         "payload": {
            "authTokenGroup": "FAMILY_PARENT",
            "requestType": "POST",
            "apiUrl": "guardian/api/buy/postpaid/prepaid-to-postpaid/fetchManageDetails/addOn/v1",
            "queryParams": {},
            "header": {},
            "body": {
               journey,
               name,
               channel,
               planId,
               simCardType,
               siNumber,
               ingress,
               siNumberManage
            }
         },
         "type": "apiCall"
      }
   ];

   return onUserAction({
      actions,
   });
};


export const redirectToNonP2p = (onUserAction: any, getState: any) => {
   const { storeValues } = getState();
   const shouldRedirect = storeValues?.redirectionContext?.shouldRedirect;

   const actions = shouldRedirect
      ? [
         {
            type: 'navigation',
            payload: {
               externalUrl:
                  'https://www.airtel.in/new-connection/postpaid-sim/new-sim?',
            },
         },
      ]
      : [];

   return onUserAction({ actions: actions });
};

export const redirectToSkyc = ({ onUserAction, getState }: any) => {
   const { storeValues } = getState();
   const shouldRedirect = storeValues?.redirectionContext?.shouldRedirect;

   const actions = shouldRedirect
      ? [
         {
            type: 'navigation',
            payload: {
               externalUrl:
                  'https://www.airtel.in/kyc/details/',
            },
         },
      ]
      : [];

   return onUserAction({ actions: actions });
};

export const updateQueryParams = ({ args, onUserAction }: any) => {
   const value = args?.[0];
   const actions = [
      {
         payload: {
            value,
            key: 'updatedQueryParams',
         },
         type: 'setDataInSessionStorage',
         target: 'session',
      },
   ];
   updateQueryParamsHelper(value);

   return onUserAction({
      actions,
   });
};

// Utils to update query params without refreshing page
export const updatePathParams = ({ args, navigate, location }: any) => {
   const data = args?.[0] || {};
   const { path, params, options } = data;

   const currUrl = window?.location.pathname;
   if (!currUrl.includes("add_details_p2p")) navigate(generatePath(path, params) + location.search, options);
};

export function useQuery() {
   return new URLSearchParams(useLocation().search);
}

/**
 * reusable method to push any config to data layer
 * @param config : any config that needs to be pushed to dataLayer
 */
export const handleGtmDataLayerEvent = ({ args, getState }: any) => {
   const config = args?.[0];
   const currPage = window?.location?.pathname;
   const { storeValues } = getState();
   const {
      nonP2PDataLayerObj,
      pageIntent,
      p2PDataLayerObj,
      omniWidgetDataLayerObj,
      user,
      selectedLob,
      cartContext,
      orderData,
      bundlingPackInfo,
      appData,
      userData
   } = storeValues;
   const leadIDNonP2P = orderData?.id;
   const cartId = cartContext?.cartId ?? '';
   const cartItemId = cartContext?.cartItemId ?? '';
   const bundleId = cartContext?.bundlingCartItemId ?? '';
   const isBundlingEnabled = bundlingPackInfo?.isBundlingEnabled;
   const childLob = selectedLob?.lob;
   const loggedInNumber = user?.loggedInNumber;
   const pageIntentValue = pageIntent?.pageIntentValue;
   const { plan_info, addons1, planName, planPrice, planData } =
      ({ ...nonP2PDataLayerObj, ...p2PDataLayerObj } as any);
   const { pincode, cityName, regionSelected } = omniWidgetDataLayerObj ?? {};
   let isAddOn = false;
   if (isSessionSupported()) {
      const journeyType = getFromSessionStorage('journeyType');
      isAddOn = 'addOn' == journeyType?.journey;
   }

   const variant = storeValues?.variantName?.variant;
   const isOmniVariant = variant === NP2P_PLANS_VARIANT

   const isSecondCall: boolean = storeValues?.addDetailsPlanAPIContext?.isSecondCall

   let msisdn = '';
   let email = '';
   if (pageIntentValue === 'p2p') {
      msisdn = appData?.msisdn ?? ""
      email = appData?.emailAddress ?? ""
   } else {
      msisdn = userData?.rtn ?? ""
   }
   if ((msisdn || '')?.length > 0) {
      msisdn = `+91${msisdn}`; // need to add country code
      msisdn = CryptoJS.SHA256(msisdn).toString(CryptoJS.enc.Hex);
   }
   if ((email || '')?.length > 0) {
      email = CryptoJS.SHA256(email).toString(CryptoJS.enc.Hex)
   }

   const nonP2pConfig = {
      pincode,
      plan_name: isOmniVariant ? (isSecondCall ? planName : '') : (planName ?? ''),
      plan_price: isOmniVariant ? (isSecondCall ? planPrice : '') : (planPrice ?? ''),
      plan_data: isOmniVariant ? (isSecondCall ? planData : '') : (planData ?? ''),
      plan_validity: '',
      region_selected: regionSelected ?? '',
      loggedInStatus: !!loggedInNumber ? 'loggedin' : 'loggedout',
      click_text: '',
      journey: ` Cpre2Post - ${pageIntentValue === 'new-connection' ? 'New Connection' : 'Port to Airtel'
         }`,
      product_name: '',
      productprice: '',
      cityname: cityName ?? '',
      plan_info: isOmniVariant ? (isSecondCall ? plan_info : '') : (plan_info ?? ''),
      addons1: addons1 ?? '',
      addons2: '',
      productcategory: 'postpaid',
      planspeed: '',
      pageName: currPage ?? '',
      LeadID: leadIDNonP2P ?? '',
      stepName: '',
      payment_Frequency: '',
      sessionId: getCookie('cs_sid')?.uuid ?? '',
      ...config,
   };

   const p2PConfig = {
      pincode,
      plan_name: planName ?? '',
      plan_price: planPrice ?? '',
      plan_data: planData ?? '',
      plan_validity: '',
      region_selected: regionSelected ?? '',
      loggedInStatus: !!loggedInNumber ? 'loggedin' : 'loggedout',
      click_text: '',
      journey: isAddOn
         ? 'addfreeconnection' + (!!childLob ? '-' + childLob : '')
         : 'commonprepaidtopostpaid' + (isBundlingEnabled ? ' - Bundled' : ''),
      product_name: '',
      productprice: '',
      cityname: cityName ?? '',
      plan_info: plan_info ?? '',
      addons1: addons1 ?? '',
      addons2: '',
      productcategory: 'postpaid',
      planspeed: '',
      pageName: currPage ?? '',
      LeadID: bundleId ? bundleId : cartItemId,
      RTNID: cartId ?? '',
      stepName: '',
      payment_Frequency: '',
      sessionId: getCookie('cs_sid')?.uuid ?? '',
      ...config,
   };

   const configData = pageIntentValue === 'p2p' || isAddOn ? p2PConfig : nonP2pConfig;

   window?.dataLayer?.push({ ...configData, msisdn, email });
};

const getCookie = (name: string) => {
   const regex = new RegExp(name + '=([^;]+)');
   const value = regex.exec(document.cookie);
   return value != null ? JSON.parse(decodeURIComponent(value[1])) : null;
};

export const callCreateCommonIntentApi = ({ args, onUserAction, getState }: any) => {
   const { queryParams = {} } = args?.[0] || {};
   let journeyType;

   const { storeValues } = getState();
   let isCoipUser = !!storeValues.userData?.isCoipUser;

   if (isSessionSupported()) {
      const intentSelected = getFromSessionStorage('intentSelected');
      journeyType = intentSelected?.type;
      if (!isCoipUser) {
         isCoipUser = !!getFromSessionStorage('isCoipUser');
      }
   }

   const actions: any = [
      {
         payload: {
            requestType: 'GET',
            apiUrl: 'guardian/api/v1/common/postpaid/createCommonIntent',
            queryParams: {
               type: journeyType,
               isCoipUser: isCoipUser,
               isNewConnection: isNewConnectionRoute(),
               ...queryParams,
            },
            header: {},
            body: {},
         },
         actionWithDelayed: 0,
         type: 'apiCall',
      },
   ];

   if (isCoipUser) {
      actions[0].payload.authTokenGroup = LOGIN_CONSTANTS.coipGroupId;
   }

   handleExitIntentFunctionality(queryParams, actions);

   return onUserAction({ actions });
};

export const handleCoipOnLoad = (onUserAction: any) => {
   let search = window.location.search;
   let params = new URLSearchParams(search);
   const userType = params.get('usertype');

   const isCoipUser = userType === 'COIP';
   const actions: any = [];

   if (isSessionSupported() && isCoipUser) {
      const emailTokens = getFromSessionStorage(LOGIN_CONSTANTS.coipGroupId);
      if (emailTokens) {
         const email = emailTokens?.email;
         const domain = email?.split('@')[1];

         //store these 2 in redux

         actions.push({
            type: 'storeValuesInRedux',
            payload: {
               context: 'userData',
               data: {
                  email,
                  domain,
                  isCoipUser,
               },
            },
         });
      }
      //need to fetch email and domain and store in redux
   }

   return onUserAction({ actions });
};

export const callCreateCommonIntentBundlingApi = ({
   args,
   onUserAction,
   getState,
}: any) => {
   const { queryParams = {}, initialCall } = args?.[0] || {};
   let journeyType;
   const storeValues = getState()?.storeValues;
   let isCoipUser = !!(storeValues.userData?.isCoipUser);
   let apiVersion = (storeValues.versionContextNP2P?.version);
   let skipApiCall = (storeValues.appData?.skipCreateCommonIntentBundlingApi);

   const variant = storeValues?.variantName?.variant ?? ""
   const variantVersion = storeValues?.variantName?.variantVersion ?? 1
   const variantContext = storeValues?.variantContext?.variant ?? ""

   let isOtpShiftLayout = false
   if (isSessionSupported()) {
      const intentSelected = getFromSessionStorage('intentSelected');
      journeyType = intentSelected?.type;
      const landingLayoutName = getFromSessionStorage("ps_landingLayoutName")
      isOtpShiftLayout = landingLayoutName == "common_bundling_otp_shift" || landingLayoutName == "p2p_bundling_otp_shift"
      if (!isCoipUser) {
         isCoipUser = !!getFromSessionStorage('isCoipUser');
      }
   }
   const apiUrl = `guardian/api/buy/postpaid/non-postpaid/createCommonIntentBundling/bundling/v${isCoipUser ? '20' : (apiVersion ? apiVersion : '1')}`;

   const actions: any = [];

   if (journeyType) {
      actions.push({
         type: "storeValuesInRedux",
         payload: {
            "context": "appData",
            "data": {
               "skipCreateCommonIntentBundlingApi": Boolean(initialCall)
            }
         }
      })
   }

   if (!skipApiCall) {
      actions.push({
         payload: {
            requestType: 'GET',
            apiUrl,
            queryParams: {
               type: journeyType,
               isCoipUser: isCoipUser,
               isOtpShiftLayout,
               variant,
               variantContext,
               variantVersion,
               isNewConnection: isNewConnectionRoute(),
               ...queryParams,
            },
            header: {},
            body: {},
         },
         type: 'apiCall',
      })

      if (isCoipUser) {
         actions[0].payload.authTokenGroup = LOGIN_CONSTANTS.coipGroupId;
      }
   }

   // handleExitIntentFunctionality(queryParams, actions);

   return onUserAction({ actions });
};

/** 
export const handleKeyboardOpen = () => {
   const listener = () => {
      const footer = document.querySelector('[data-testid="artist-sticky-footer"]');
      const viewPortHeight = window?.visualViewport?.height;
      if (!footer || !viewPortHeight) return;

      const minKeyboardHeight = 250;
      const isKeyboardOpen = window?.screen?.height - minKeyboardHeight > viewPortHeight;

      if (isKeyboardOpen) {
         footer.classList?.add('hide-artist-footer');
      } else {
         footer.classList?.remove('hide-artist-footer');
      }
   };

   if (typeof visualViewport != 'undefined' && !isNewIOSApp()) {
      window?.visualViewport?.addEventListener('resize', listener);
   }
};
**/

export const handleKeyboardOpen = () => {
   let initialHeight = window.visualViewport?.height;
   const listener = () => {
      const footer = document.querySelector('[data-testid="artist-sticky-footer"]');
      const currentHeight = window.visualViewport?.height;

      if (!footer || !initialHeight || !currentHeight) return;

      const heightDiff = initialHeight - currentHeight;
      const isKeyboardOpen = heightDiff > 150;

      if (isKeyboardOpen) {
         footer.classList.add('hide-artist-footer');
      } else {
         footer.classList.remove('hide-artist-footer');
      }
   };
   if (typeof visualViewport != 'undefined' && !isNewIOSApp()) {
      window?.visualViewport?.addEventListener('resize', listener);
   }
};

export const handleExitIntentOnSubmit = (onUserAction: any, getState: any) => {
   const storeValues = getState()?.storeValues;
   const feedbackSelected = storeValues.exitIntentData?.feedbackSelected;
   const feedbackInput = storeValues.exitIntentData?.feedbackInput;
   const actions: any = [];

   const submitAnalyticsObj = {
      analyticsAction: 'click',
      analyticsData: {
         section: 'ExitIntentSection',
         subSection: 'SubmitButtonWidget',
         eventAction: 'click',
         eventCategory: 'common_add_details_page',
         eventLabel: 'submit',
         eventValue: 'submit',
         prop1: feedbackSelected,
         evar21: '{{storeValues:omniWidgetAnalyticsData.leadRtn}}',
         prop12: '{{storeValues:familyFormData.userBasicDetails.name}}',
         prop13: '{{storeValues:userSelectedAddress.addressString}}',
         city: '{{storeValues:userSelectedAddress.city}}',
         evar8: '{{storeValues:cartContext.cartId}}',
         extradata12: '{{storeValues:cartContext.cartItemId}}',
      },
   };

   actions.push(submitAnalyticsObj);

   const thanksAnalyticsObj = {
      analyticsAction: 'impression',
      analyticsData: {
         section: 'ExitIntentSection',
         subSection: 'SubmitButtonWidget',
         eventAction: 'impression',
         eventCategory: 'common_add_details_page',
         eventLabel: 'feedback thank you',
         eventValue: 'feedback thank you',
         prop1: feedbackSelected,
         evar21: '{{storeValues:omniWidgetAnalyticsData.leadRtn}}',
         prop12: '{{storeValues:familyFormData.userBasicDetails.name}}',
         prop13: '{{storeValues:userSelectedAddress.addressString}}',
         city: '{{storeValues:userSelectedAddress.city}}',
         evar8: '{{storeValues:cartContext.cartId}}',
         extradata12: '{{storeValues:cartContext.cartItemId}}',
      },
   };

   actions.push(thanksAnalyticsObj);

   return onUserAction({ actions });
};

export const handleExitIntentFunctionality = (selectedIntent: any, actions: any) => {
   const value = selectedIntent?.type || '';
   actions.push({
      type: 'toggleExitIntent',
      payload: {
         enable: value === 'p2p' || value === '' ? false : true,
      },
   });
};

export const handleExitIntentFeedbackSelect = (
   onUserAction: any,
   getState: any,
   args: { value: string; id: string }
) => {
   const storeValues = getState()?.storeValues;
   const feedbackSelectedFlag = storeValues.exitIntentData?.feedbackSelectedFlag;
   if (!feedbackSelectedFlag) return;

   const { value = '', id = '' } = args;

   const submitButtonWidgetData: any = {
      withBreakpointProps: {
         xl: {
            leftCta: {
               buttonType: 'primary',
               style: {
                  backgroundColor: '#D73737',
                  width: '335px',
                  maxWidth: '335px',
               },
               title: 'Submit',
               version: 'fixed-padding',
               controlState: 'none',
            },
            style: {
               paddingVertical: 20,
               alignItems: 'flex-end',
               paddingHorizontal: 0,
            },
         },
         sm: {
            leftCta: {
               buttonType: 'primary',
               style: {
                  backgroundColor: '#D73737',
                  width: '100%',
                  maxWidth: '100%',
               },
               title: 'Submit',
               version: 'variable-width',
               controlState: 'none',
            },
            style: {
               paddingVertical: 20,
               alignItems: 'center',
               paddingHorizontal: 0,
            },
         },
         lg: {
            leftCta: {
               buttonType: 'primary',
               style: {
                  backgroundColor: '#D73737',
                  width: '335px',
                  maxWidth: '335px',
               },
               title: 'Submit',
               version: 'fixed-padding',
               controlState: 'none',
            },
            style: {
               paddingVertical: 20,
               alignItems: 'flex-end',
               paddingHorizontal: 0,
            },
         },
         xs: {
            leftCta: {
               buttonType: 'primary',
               style: {
                  backgroundColor: '#D73737',
                  width: '100%',
                  maxWidth: '100%',
               },
               title: 'Submit',
               version: 'variable-width',
               controlState: 'none',
            },
            style: {
               paddingVertical: 20,
               alignItems: 'center',
               paddingHorizontal: 0,
            },
         },
      },
   };

   const inputWidgetData = {
      value1: value,
      id: id,
      hideOnPlatforms: ['mweb', 'dweb', 'android-webview', 'ios-webview'],
      value: '',
   };

   const filterListWidgetData: any = {
      'titleSectionProps.style.borderBottomLeftRadius': 18,
      'titleSectionProps.style.borderBottomRightRadius': 18,
      'titleSectionProps.style.borderBottom': '1px solid rgb(218, 223, 231)',
      checkedRadioId: id,
   };

   const storeInReduxData = {
      feedbackSelected: value,
      feedbackInput: '',
   };

   const analyticsData = {
      eventValue: value,
      eventCategory: 'common_add_details_page',
      section: 'ExitIntentSection',
      subSection: 'feedbackWidget',
      eventAction: 'click',
      eventLabel: 'reason',
   };

   if (value.toLowerCase() === UTIL_CONSTANTS.OTHER) {
      ['lg', 'sm', 'xl', 'xs'].forEach((currSize: string) => {
         submitButtonWidgetData.withBreakpointProps[currSize].leftCta.buttonType =
            'secondary';
         submitButtonWidgetData.withBreakpointProps[currSize].leftCta.controlState =
            'disabled';
         submitButtonWidgetData.withBreakpointProps[
            currSize
         ].leftCta.style.backgroundColor = '#DADFE7';
      });

      inputWidgetData.hideOnPlatforms = [];
      filterListWidgetData['titleSectionProps.style.borderBottomLeftRadius'] = 0;
      filterListWidgetData['titleSectionProps.style.borderBottomRightRadius'] = 0;
      filterListWidgetData['titleSectionProps.style.borderBottom'] = 'none';
   }

   const submitButtonWidgetAction = {
      payload: {
         eventData: submitButtonWidgetData,
         eventName: 'submitButtonWidget',
      },
      type: 'nativeEventEmitter',
   };

   const inputWidgetAction = {
      payload: {
         eventData: inputWidgetData,
         eventName: 'inputWidget',
      },
      type: 'nativeEventEmitter',
   };

   const filterListWidgetAction = {
      payload: {
         eventData: filterListWidgetData,
         eventName: 'filterListWidget',
      },
      type: 'nativeEventEmitter',
   };

   const storeInReduxAction = {
      payload: {
         data: storeInReduxData,
         context: 'exitIntentData',
      },
      type: 'storeValuesInRedux',
   };

   const analyticsAction = {
      analyticsAction: 'click',
      analyticsData,
   };

   const actions: any = [
      submitButtonWidgetAction,
      inputWidgetAction,
      filterListWidgetAction,
      storeInReduxAction,
      analyticsAction,
   ];

   return onUserAction({ actions });
};

export const handleWebViewCloseActionForManageAddOnThanksPage = () => {
   const { journey } = getQueryString();
   let channelFromSession = window?.sessionStorage?.getItem("channel")
   let isManageAddOnThanksPage = journey == "addOn" && channelFromSession == "THANKS_APP" && window.location && (window.location.pathname?.includes("thank-you") || window.location.pathname?.includes("thanks-add-child-details-new"))

   if (isManageAddOnThanksPage) {
      if (isNewIOSApp()) {
         //@ts-ignore
         window?.webkit?.messageHandlers?.openDeeplink?.postMessage("myairtel://home")
      }
      else {
         //@ts-ignore
         window?.MyAirtelAppReact?.launch("myairtel://home")
      }
   }
   else {
      closeWebView?.();
   }
   removeTokensFromSessionStorage("channel")
}

export const resetOnWebViewCloseCallback = ({ args, onUserAction, getState }: any) => {
   const search = window.location.search;
   const params = new URLSearchParams(search);
   const journey = params.get('journey');
   let channelFromSession = window?.sessionStorage?.getItem("channel")
   if (channelFromSession == "THANKS_APP") {
      if (isMobileApp()) {
         if (isNewIOSApp()) {
            //@ts-ignore
            window?.webkit?.messageHandlers?.triggerAppEvent?.postMessage?.({
               eventName: 'disableCustomBackPress'
            });

         } else {
            //@ts-ignore
            if (window?.MyAirtelAppReact?.customBackPress) {
               //@ts-ignore
               window?.MyAirtelAppReact?.customBackPress(
                  'customBackPressCallback',
                  false
               );
            }
         }
      }
   }
};

export const redirectToManageChildLandingPage = () => {
   return {
      payload: {
         type: 'replace',
         id: 'redirection',
         screenName: '/add-child-details-new',
         shouldAddSearchParams: true,
         params: {},
      },
      actionWithDelayed: 300,
      type: 'navigation',
   }
};

export const callFetchQuotaAndPlanDetails = (parentOrderId: string, channel?: string, siNumber?: string) => {
   return {
      "payload": {
         "authTokenGroup": "FAMILY_PARENT",
         "requestType": "GET",
         "apiUrl": "guardian/api/buy/postpaid/prepaid-to-postpaid/fetchPlanAndQuotaDetails/v2",
         "queryParams": {
            "orderId": parentOrderId
         },
         "onError": {
            "actions": [
               {
                  "type": "updateWidgetProps",
                  "payload": {
                     "widgetId": "LoaderWidgetChildNew",
                     "widgetData": {
                        "showErrorStatus": true
                     }
                  }
               },
               {
                  "analyticsAction": "ERROR",
                  "analyticsData": {
                     "eVar32": "Error while calling FetchPlanAndQuotaDetails"
                  }
               }
            ]
         },
         "headers": {
            "channel": channel,
            "sinumber": siNumber
         },
         "body": {
         }
      },
      "type": "apiCall"
   };
};

export const callFetchOrdersByRtn = (data: any) => {
   return {
      "payload": {
         "authTokenGroup": "FAMILY_PARENT",
         "requestType": "GET",
         "apiUrl": "guardian/api/buy/postpaid/prepaid-to-postpaid/fetchOrdersByRtn/v2",
         "queryParams": {
            "channel": data?.channel,
            "journey": data?.journey
         },
         "onError": {
            "actions": [
               {
                  "type": "updateWidgetProps",
                  "payload": {
                     "widgetId": "LoaderWidgetChildNew",
                     "widgetData": {
                        "showErrorStatus": true
                     }
                  }
               },
               {
                  "analyticsAction": "ERROR",
                  "analyticsData": {
                     "eVar32": "Error while calling fetchOrdersByRtn"
                  }
               }
            ]
         },
         "header": {}
      },
      "type": "apiCall"
   };
};

export const callFetchParentAndChildPlan = (planId?: string) => {
   return {
      "payload": {
         "authTokenGroup": "FAMILY_PARENT",
         "requestType": "GET",
         "apiUrl": "guardian/api/buy/postpaid/prepaid-to-postpaid/fetchParentAndChildPlan/v2",
         "queryParams": {
            "planId": planId || "{{storeValues:orderContext.planId}}"
         },
         "onError": {
            "actions": [
               {
                  "type": "updateWidgetProps",
                  "payload": {
                     "widgetId": "LoaderWidgetChildNew",
                     "widgetData": {
                        "showErrorStatus": true
                     }
                  }
               },
               {
                  "analyticsAction": "ERROR",
                  "analyticsData": {
                     "eVar32": "Error while calling fetchParentAndChildPlan"
                  }
               }
            ]
         },
         "header": {}
      },
      "type": "apiCall"
   };
};

const checkPlanAndQuota = (availableQuota: number, actions: any[], parentOrderId: string, channel?: string, siNumber?: string, planId?: string) => {
   if (availableQuota > 0) {
      actions.push(callFetchParentAndChildPlan(planId))
   } else {
      actions?.push(callFetchQuotaAndPlanDetails(parentOrderId, channel, siNumber))
   }
}

const callUserManagement = (actions: any[], siNumber: string, name: string, planId?: string, journey?: string, channel?: string, source?: string, agentId?: string, role?: string, isDeepLink?: boolean, availableQuota?: number) => {
   const callUserManagementAction = {
      "payload": {
         "authTokenGroup": "FAMILY_PARENT",
         "requestType": "POST",
         "apiUrl": "guardian/api/buy/postpaid/prepaid-to-postpaid/fetchUserManagement/v2",
         "queryParams": {},
         "header": {},
         "onError": {
            "actions": [
               {
                  "type": "updateWidgetProps",
                  "payload": {
                     "widgetId": "LoaderWidgetChildNew",
                     "widgetData": {
                        "showErrorStatus": true
                     }
                  }
               },
               {
                  "analyticsAction": "ERROR",
                  "analyticsData": {
                     "eVar32": "Error while calling fetchUserManagement"
                  }
               }
            ]
         },
         "body": {
            journey,
            name,
            channel,
            planId,
            siNumber,
            availableQuota,
            source,
            agentId,
            role,
            isDeepLink
         }
      },
      "type": "apiCall"
   }
   actions?.push(callUserManagementAction)
}

export const initializeManageChildJourney = ({ args, onUserAction, getState }: any) => {
   const { storeValues } = getState();
   const {
      orderContext,
      parentContext,
   } = storeValues;
   const parentOrderId = orderContext?.parentOrderId
   const totalAddOnQuota = parentContext?.totalAddOnQuota
   const addOnQuotaUsed = parentContext?.addOnQuotaUsed
   const availableQuota = totalAddOnQuota - addOnQuotaUsed
   const actions: any[] = []
   const operationType = args?.operationType
   let { siNumber, name, planId, journey, channel, source, agentId, role, isDeepLink } = getQueryString();

   siNumber = siNumber ? siNumber : storeValues?.additionalData?.queryParams?.siNumber

   if (siNumber && name && planId && journey && channel == "THANKS_APP" && journey == "MANAGE_FAMILY") {
      actions.push({
         type: "setDataInSessionStorage",
         target: "session",
         payload: {
            "key": "channel",
            "value": channel
         }
      })
      callUserManagement(actions, siNumber, name, planId, journey, channel, source, agentId, role, isDeepLink, availableQuota)
      return onUserAction({
         actions
      });
   }

   if (operationType === "checkPlanAndQuota") {
      checkPlanAndQuota(availableQuota, actions, parentOrderId)
      return onUserAction({
         actions
      });
   }
   else if (parentOrderId) {
      checkPlanAndQuota(availableQuota, actions, parentOrderId)
      return onUserAction({
         actions
      });
   } else {
      actions?.push(callFetchOrdersByRtn({ channel, journey }))
      return onUserAction({
         actions
      });
   }

}

export const handleAddOnDetailsOnLoad = ({ args, onUserAction, getState }: any) => {
   const actions: any[] = []
   let { channel } = getQueryString();
   const isManageJourney = channel === "THANKS_APP";

   actions.push({
      type: "apiCall",
      actionWithDelayed: 0,
      payload: {
         authTokenGroup: "FAMILY_PARENT",
         headers: {},
         onError: {},
         requestType: "POST",
         apiUrl:
            "guardian/api/buy/postpaid/prepaid-to-postpaid/fetchManagedConnection/v2",
         queryParams: {},
         body: {
            eligibilityRequestParams: {
               addOnPlanId:
                  "{{storeValues:selectedPlanContext.childPlan.childPlanId}}",
               parentPlanId: "{{storeValues:orderContext.planId}}",
               orderItemId: "{{storeValues:orderContext.orderItemId}}",
            },
            channel: "{{sessionStorage:channel}}",
            source: "{{storeValues:appData.source}}",
            ...(isManageJourney && {
               parentRtn: "{{storeValues:rtn.parentRtn}}",
            }),
         },
      },
   });

   return onUserAction({
      actions
   });
}

export const getContactsUtil = (onUserAction: any) => {
   getContacts().then((contacts: any) => {
      if (Array.isArray(contacts) && contacts.length > 0) {
         const data = contacts.map((contact: any) => ({
            "title": contact.name,
            "subtitle": contact.msisdn
         }))

         onUserAction({
            actions: [
               {
                  "type": "updateWidgetProps",
                  "payload": {
                     "widgetId": "childNumberInput",
                     "widgetData": {
                        "autoCompleteData": data,
                        "enableAutocomplete": true,
                        "autoCompleteFilterKey": "title",
                        "autoCompleteValueKey": "subtitle"
                     }

                  }
               }
            ],
         });
      }
   });
};

export const getContactsUtilP2P = (onUserAction?: any) => {

   let contactsFetched = false

   getContacts().then((contacts: any) => {

      if (Array.isArray(contacts) && contacts.length > 0) {
         const data = contacts.map((contact: any, index: number) => ({
            "heading": contact.name,
            "subHeading": contact.msisdn,
            "value": extractIndianMobileNumber(contact.msisdn)
         }))

         contactsFetched = true;
         onUserAction({
            actions: [
               {
                  type: 'storeValuesInRedux',
                  payload: {
                     context: 'contactDetailsContext',
                     data: {
                        'contactsAllowed': contactsFetched
                     },
                  },
               },
               {
                  "type": "updateWidgetProps",
                  "payload": {
                     "widgetId": "ContactDetailsWidget",
                     "widgetData": {
                        "dynamicFieldsProps.alternateNumber.dropdownList": data,
                     }
                  }
               }
            ],
         });
      } else {
         onUserAction({
            actions: [
               {
                  type: 'storeValuesInRedux',
                  payload: {
                     context: 'contactDetailsContext',
                     data: {
                        'contactsAllowed': contactsFetched
                     },
                  },
               },
            ],
         });

      }
   })
};

export const showMetaInfoTag = (onUserAction?: any) => {

      onUserAction({
         actions: [
            {
               type: 'updateWidgetProps',
               payload: {
                  widgetId: 'lead_contact_details_step3',
                  widgetData: {
                     'listData.metaInfoTag': 'Unlimited data + top OTTs',
                     'listData.metaInfoTagType': 'offer',
                  },
               },
            },
         ],
      });

}

export const generateChangeActions = ({ args, data, onUserAction, getState }: any) => {
   const currStepName = args?.[0]?.stepName as string;
   const { storeValues } = getState();

   const subHeading = storeValues?.selectedPlanContext?.subHeading
   const hasPlanStepOpenedOnce = storeValues?.selectedPlanContext?.hasPlanStepOpenedOnce
   const containsCircle = storeValues?.circleDetails?.containsCircle
   const stepsName = [
      'StepWidget_1',
      'EnterMobileNumberWidget',
      'ContactDetailsWidget',
      'AddressDetailsWidget',
      'SelectPostpaidPlanWidget'
   ];

   const actions: any = stepsName.map((eventName) => {
      if (eventName !== currStepName) {
         return {
            type: 'nativeEventEmitter',
            payload: {
               eventName: eventName,
               eventData: {
                  isExpanded: false,
               },
            },
         };
      }

      return {
         type: 'nativeEventEmitter',
         payload: {
            eventName: currStepName,
            eventData: {
               isExpanded: true,
            },
         },
      };
   });
   if (currStepName != "SelectPostpaidPlanWidget") {
      actions.push({
         type: 'nativeEventEmitter',
         payload: {
            eventName: "SelectPostpaidPlanWidget",
            eventData: {
               "listData.subHeading": hasPlanStepOpenedOnce ? subHeading : "",
               "listData.metaInfoTag": !containsCircle ? "Extra 25GB unlocked" : "",
            },
         },
      })
   } else {
      actions.push({
         type: 'nativeEventEmitter',
         payload: {
            eventName: "SelectPostpaidPlanWidget",
            eventData: {
               "listData.metaInfoTag": "",
            },
         },
      })
   }

   actions.push({
      analyticsAction: "click",
      analyticsData: {
         eVar0: "p2p_safo p2p change cta " + currStepName,
         eventCategory: "common_add_details_page",
         section: "stepcardchangecta",
         subSection: currStepName,
         eventAction: "click",
         eVar42: "{{storeValues:userData.userStatus}}",
         eVar17: "{{storeValues:userData.userInfo}}",
         eVar41: "{{storeValues:userData.flow}}",
         category: "{{sessionStorage:intentSelected.type}}",
         cartId: "{{storeValues:cartContext.cartItemId}}",
         ticketId: "{{storeValues:cartContext.cartId}}",
         email: "{{storeValues:appData.emailAddress}}",
         eVar20: "{{storeValues:user.loggedInNumber}}",
         eVar21: "{{storeValues:appData.msisdn}}",
         eVar36: "{{storeValues:appData.alternateNumber}}"
      }
   })

   return onUserAction({
      actions: actions,
   });

};

const extractIndianMobileNumber = (phoneString: String) => {
   if (!phoneString || typeof phoneString !== 'string') {
      return '';
   }

   // Remove all non-numeric characters
   const cleanNumber = phoneString.replace(/\D/g, '');

   // Handle different formats
   if (cleanNumber.length === 10 && /^[6-9]/.test(cleanNumber)) {
      // Direct 10-digit number starting with 6-9: 9876543210
      return cleanNumber;
   }

   if (cleanNumber.length === 11 && cleanNumber.startsWith('0')) {
      // 11-digit with leading 0: 09876543210
      const without0 = cleanNumber.substring(1);
      if (/^[6-9]/.test(without0)) {
         return without0;
      }
   }

   if (cleanNumber.length === 12 && cleanNumber.startsWith('91')) {
      // 12-digit with country code 91: 919876543210, +919876543210
      const withoutCountryCode = cleanNumber.substring(2);
      if (/^[6-9]/.test(withoutCountryCode)) {
         return withoutCountryCode;
      }
   }

   if (cleanNumber.length === 13 && cleanNumber.startsWith('091')) {
      // 13-digit with 091: 0919876543210
      const withoutCountryCode = cleanNumber.substring(3);
      if (/^[6-9]/.test(withoutCountryCode)) {
         return withoutCountryCode;
      }
   }

   // Handle international formats like +91 (0) or other variations
   if (cleanNumber.length > 10) {
      // Try to extract last 10 digits if they form a valid Indian mobile number
      const last10 = cleanNumber.slice(-10);
      if (/^[6-9]\d{9}$/.test(last10)) {
         return last10;
      }

      // Try extracting 10 digits after removing common prefixes
      const patterns = ['91', '091', '0091', '00091'];
      for (const pattern of patterns) {
         if (cleanNumber.startsWith(pattern)) {
            const remaining = cleanNumber.substring(pattern.length);
            // Remove any leading zeros after country code
            const withoutLeadingZeros = remaining.replace(/^0+/, '');
            if (withoutLeadingZeros.length === 10 && /^[6-9]/.test(withoutLeadingZeros)) {
               return withoutLeadingZeros;
            }
         }
      }
   }

   // Return empty string if no valid pattern found
   return '';
};

export const getLoggedInUserNumber = async () => {
   const isApp = isMobileApp();
   let number = ""
   if (isApp) {
      try {
         number = await getLoggedInNumber();
      } catch (error) {
         fireLogWithSentryBase(
            "commonUtils.ts → error while fetching logged-in number in APP",
            error
         );
      }
   } else {
      const isWebLoggedInUser = isUserLoggedIn();
      if (isWebLoggedInUser) {
         number = getLoggedInUser();
      }
   }
   return number || ""
};

/**
 * method to mark firebase event
 * @param args : eventName and eventData to be sent to firebase
 */
export const handleMarkFirebaseEvent = (args: any, getState: any) => {
   try {
      const eventName: string = args?.eventName;
      const eventData = args?.eventData || {};
      if (eventName) {
         markFirebaseEvent(eventName, eventData);
      }
   } catch (err) {
      fireLogWithSentryBase(
         'commonUtils.ts-->handleMarkFirebaseEvent-->error while marking firebase event',
         err
      );
   }
};

export const updateIsBundlingAppliedInRedux = (args: any, onUserAction: any, getState: any) => {
   const { storeValues, widgetDataStore } = getState()
   const isFromRemoveModal = args?.isFromRemoveModal;
   const isBundlingApplied = storeValues?.appData?.isBundlingApplied;

   const { common_add_details_page_SelectPostpaidPlanWidget } = widgetDataStore;

   const planListData = common_add_details_page_SelectPostpaidPlanWidget?.planListWidgetData?.planListData

   const actions = [];

   const oneAirtelAppliedMethod = "manual"

   if (isBundlingApplied && !isFromRemoveModal) {
      // CASE 1: Bundling is applied → show modal asking to remove
      actions.push({
         type: "openModal",
         actionWithDelayed: 0,
         payload: {
            modalData: {},
            modalSection: "RemoveBundlingOfferIntent",
         },
      });
      actions.push(
         {
            "analyticsAction": "pageloaded",
            "analyticsData": {
               "eVar0": "p2p_safo_oab_want to remove your one airtel bundle modal load",
               "eventAction": "pageloaded",
               "eventCategory": "common_add_details_page",
               "section": "entermobilenumbersection",
               "subSection": "entermobilenumberwidget",
               "eventLabel": "unlocked 100 off coupon",
               "eventValue": "applicable on every one airtel bill payment",
               "eVar42": "high_value",
               "prop45": "p2p",
               "prop26": oneAirtelAppliedMethod,
               "extraData6": "applied oneairtel",
               "prop17": "oneairtel",
               "prop28": "{{storeValues:userData.contactFieldsVisible}}",
               "eVar17": "{{storeValues:userData.userInfo}}",
               "eVar41": "{{storeValues:userData.flow}}",
               "category": "{{sessionStorage:intentSelected.type}}",
               "cartId": "{{storeValues:cartContext.cartItemId}}",
               "ticketId": "{{storeValues:cartContext.cartId}}",
               "email": "{{storeValues:appData.emailAddress}}",
               "eVar20": "{{storeValues:user.loggedInNumber}}",
               "eVar21": "{{storeValues:appData.msisdn}}",
               "eVar36": "{{storeValues:appData.alternateNumber}}",
               "eVar25": "{{storeValues:userData.savedAddress.circleId}}",
               "eVar33": "{{storeValues:userData.savedAddress.postCode}}",
               "prop5": "{{storeValues:selectedPlanContext.planPrice}}",
               "eVar15": ""
            }
         }
      )
      actions.push({
         "analyticsAction": "click",
         "analyticsData": {
            "eVar0": "p2p_safo_oab_one airtel bundle remove click",
            "eventAction": "click",
            "eventCategory": "common_add_details_page",
            "eventLabel": "remove",
            "customLabel": "button",
            "eventValue": "one airtel plan",
            "eVar42": "high_value",
            "prop45": "p2p",
            "prop26": oneAirtelAppliedMethod,
            "extraData6": "applied oneairtel",
            "prop17": "oneairtel",
            "prop28": "{{storeValues:userData.contactFieldsVisible}}",
            "eVar17": "{{storeValues:userData.userInfo}}",
            "eVar41": "{{storeValues:userData.flow}}",
            "category": "{{sessionStorage:intentSelected.type}}",
            "cartId": "{{storeValues:cartContext.cartItemId}}",
            "ticketId": "{{storeValues:cartContext.cartId}}",
            "email": "{{storeValues:appData.emailAddress}}",
            "eVar20": "{{storeValues:user.loggedInNumber}}",
            "eVar21": "{{storeValues:appData.msisdn}}",
            "eVar36": "{{storeValues:appData.alternateNumber}}",
            "eVar25": "{{storeValues:userData.savedAddress.circleId}}",
            "eVar33": "{{storeValues:userData.savedAddress.postCode}}",
            "prop5": "{{storeValues:selectedPlanContext.planPrice}}",
            "eVar15": ""
         }
      })
   } else {
      // planListData?.forEach((plansData: {
      //    priceDetails: { price: string; offerText: string; oldPrice: string };
      //    buttonWidgetData: { onLeftCtaPress: any; onRightCtaPress: any }
      //    onCardPress: any
      // }) => {

      //    const onLeftCtaPress = plansData?.buttonWidgetData?.onLeftCtaPress
      //    const onRightCtaPress = plansData?.buttonWidgetData?.onRightCtaPress
      //    const onCardPress = plansData?.onCardPress

      //    if (isBundlingApplied && isFromRemoveModal) {
      //       plansData.priceDetails.oldPrice = ""
      //       plansData.priceDetails.price = `₹${Number(plansData?.priceDetails?.price.slice(1)) + 100}`
      //       plansData.priceDetails.offerText = ""
      //    } else {
      //       plansData.priceDetails.oldPrice = plansData?.priceDetails?.price
      //       plansData.priceDetails.price = `₹${Number(plansData?.priceDetails?.price.slice(1)) - 100}`
      //       plansData.priceDetails.offerText = "Save ₹100"
      //    }
      //    onLeftCtaPress.actions[0].payload.widgetData.planInfoData.amount = `${plansData.priceDetails.price}/month`
      // })
      // CASE 2: Toggle bundling & update UI
      actions.push(
         {
            type: "storeValuesInRedux",
            payload: {
               context: "appData",
               data: {
                  isBundlingApplied: !isBundlingApplied,
                  isOneAirtelOrStandalone: isBundlingApplied ? "standalone" : "oneairtel",
                  oneAirtelAppliedMethod: oneAirtelAppliedMethod
               },
            },
         },
         {
            type: "updateWidgetProps",
            actionWithDelayed: 0,
            payload: {
               widgetId: "BundlingOfferWidget",
               widgetData: {
                  metaInfoCta: isBundlingApplied ? "Apply Now" : "Remove",
                  metaInfoCtaProps: {
                     titleProps: {
                        text: isBundlingApplied ? "Apply Now" : "Remove"
                     },
                     "noHoverEffect": true
                  },
                  headingInfoTag: isBundlingApplied ? "" : "Applied",
               },
               shouldResolveData: true,
               pageName: "",
            },
         },
         {
            type: "closeModal",
            actionWithDelayed: 0,
            payload: {
               modalData: {},
               modalSection: "RemoveBundlingOfferIntent",
            },
         },
         {
            "analyticsAction": "click",
            "analyticsData": {
               "eVar0": !isBundlingApplied ? "p2p_safo_oab_apply now click" : "",
               "eventAction": "click",
               "eventCategory": "common_add_details_page",
               "eventLabel": !isBundlingApplied ? "apply now" : "",
               "customLabel": "button",
               "eventValue": !isBundlingApplied ? "100 off coupon" : "",
               "eVar42": "high_value",
               "prop45": "p2p",
               "prop26": oneAirtelAppliedMethod,
               "extraData6": "applied oneairtel",
               "prop17": "oneairtel",
               "prop28": "{{storeValues:userData.contactFieldsVisible}}",
               "eVar17": "{{storeValues:userData.userInfo}}",
               "eVar41": "{{storeValues:userData.flow}}",
               "category": "{{sessionStorage:intentSelected.type}}",
               "cartId": "{{storeValues:cartContext.cartItemId}}",
               "ticketId": "{{storeValues:cartContext.cartId}}",
               "email": "{{storeValues:appData.emailAddress}}",
               "eVar20": "{{storeValues:user.loggedInNumber}}",
               "eVar21": "{{storeValues:appData.msisdn}}",
               "eVar36": "{{storeValues:appData.alternateNumber}}",
               "eVar25": "{{storeValues:userData.savedAddress.circleId}}",
               "eVar33": "{{storeValues:userData.savedAddress.postCode}}",
               "prop5": "{{storeValues:selectedPlanContext.planPrice}}",
               "eVar15": ""
            }
         }
      );
      actions.push({
         analyticsAction: 'click',
         analyticsData: {
            eVar0: !isBundlingApplied ? 'p2p_safo_oab_apply now click' : '',
            eventAction: 'click',
            eventCategory: 'common_add_details_page',
            eventLabel: !isBundlingApplied ? 'apply now' : '',
            customLabel: 'button',
            eventValue: !isBundlingApplied ? '100 off coupon' : '',
            eVar42: 'high_value',
            prop45: 'p2p',
            prop26: oneAirtelAppliedMethod,
            extraData6: 'applied oneairtel',
            prop17: 'oneairtel',
            prop28: "{{storeValues:userData.contactFieldsVisible}}",
            eVar17: '{{storeValues:userData.userInfo}}',
            eVar41: '{{storeValues:userData.flow}}',
            category: '{{sessionStorage:intentSelected.type}}',
            cartId: '{{storeValues:cartContext.cartItemId}}',
            ticketId: '{{storeValues:cartContext.cartId}}',
            email: '{{storeValues:appData.emailAddress}}',
            eVar20: '{{storeValues:user.loggedInNumber}}',
            eVar21: '{{storeValues:appData.msisdn}}',
            eVar36: '{{storeValues:appData.alternateNumber}}',
            eVar25: '{{storeValues:userData.savedAddress.circleId}}',
            eVar33: '{{storeValues:userData.savedAddress.postCode}}',
            prop5: '{{storeValues:selectedPlanContext.planPrice}}',
            eVar15: ''
         }
      })
   }

   actions.push(
      {
         "actionWithDelayed": 0,
         "payload": {
            "widgetId": "SelectPostpaidPlanWidget",
            "widgetData": {
               "planListWidgetData.planListData": planListData
            },
            "shouldResolveData": true,
            "pageName": ""
         },
         "type": "updateWidgetProps"
      }
   )

   return onUserAction({ actions });

};

export const updatePlanDetailsOnBundlingApplied = ({ args, onUserAction, getState }: any) => {
   const { widgetDataStore } = getState()
   const isBundlingApplied = args?.isBundlingApplied;
   const bundlingMasterEligibility = args?.bundlingMasterEligibility;


   const { common_add_details_page_SelectPostpaidPlanWidget } = widgetDataStore;

   const planListData = common_add_details_page_SelectPostpaidPlanWidget?.planListWidgetData?.planListData

   const actions = [];

   planListData?.forEach((plansData: {
      priceDetails: { price: string; offerText: string; oldPrice: string };
      buttonWidgetData: { onLeftCtaPress: any; onRightCtaPress: any }
      onCardPress: any
   }) => {
      if (!isBundlingApplied && bundlingMasterEligibility) {
         plansData.priceDetails.oldPrice = plansData?.priceDetails?.price
         plansData.priceDetails.price = `₹${Number(plansData?.priceDetails?.price.slice(1)) - 100}`
         plansData.priceDetails.offerText = "Save ₹100"

         const onLeftCtaPress = plansData?.buttonWidgetData?.onLeftCtaPress
         const onRightCtaPress = plansData?.buttonWidgetData?.onRightCtaPress
         const onCardPress = plansData?.onCardPress

         onLeftCtaPress.actions[0].payload.widgetData.planInfoData.amount = `${plansData.priceDetails.price}/month`

      }
   })

   actions.push(
      {
         "actionWithDelayed": 0,
         "payload": {
            "widgetId": "SelectPostpaidPlanWidget",
            "widgetData": {
               "planListWidgetData.planListData": planListData
            },
            "shouldResolveData": true,
            "pageName": ""
         },
         "type": "updateWidgetProps"
      }
   )

   return onUserAction({ actions });

};

export const handleTabChange = ({ args, onUserAction, actionContextData, getState }: any) => {
   const actions: any[] = []
   const { storeValues } = getState();
   const {
      user
   } = storeValues;
   const loggedInNumber = user?.loggedInNumber;
   let index = actionContextData?.index
   let currentPlanContext: {
      planPrice: string;
      planId: string;
      planName: string;
      plan_data: string;
      plan_info: string;
      addons1: string;
   } = {
      planPrice: '',
      planId: '',
      planName: '',
      plan_data: '',
      plan_info: '',
      addons1: '',
   };
   if (index == 0) {
      actions.push(
         {
            type: "updateWidgetProps",
            payload: {
               widgetId: "PriorityPostpaidTabsWidget",
               widgetData: {
                  "data[0].style": {
                     "background": "linear-gradient(137.68deg, #E80000 2.55%, #F90000 27.64%, #FF4100 89.69%)"
                  },
                  "data[1].style": {
                     "background": ""
                  },
               }
            }
         }
      )
      currentPlanContext = { ...storeValues?.IndividualPlanDetails }
   } else {
      actions.push(
         {
            type: "updateWidgetProps",
            payload: {
               widgetId: "PriorityPostpaidTabsWidget",
               widgetData: {
                  "data[1].style": {
                     "background": "linear-gradient(137.68deg, #E80000 2.55%, #F90000 27.64%, #FF4100 89.69%)"
                  },
                  "data[0].style": {
                     "background": ""
                  },
               }
            }
         }
      )
      currentPlanContext = { ...storeValues?.FamilyPlanDetails }
   }

   actions.push(
      {
         type: "storeValuesInRedux",
         payload: {
            context: "currentPlanContext",
            data: {
               planPrice: currentPlanContext?.planPrice,
               planId: currentPlanContext?.planId,
               planName: currentPlanContext?.planName,
               plan_data: currentPlanContext?.plan_data,
               plan_info: currentPlanContext?.plan_info,
               addons1: currentPlanContext?.addons1,
            }
         }
      }
   )

   actions.push({
      payload: {
         args: [
            {
               plan_name: currentPlanContext?.planName,
               plan_price: currentPlanContext?.planPrice,
               plan_data: currentPlanContext?.plan_data,
               plan_info: currentPlanContext?.plan_info,
               addons1: currentPlanContext?.addons1,
               event: index === 0 ? 'individual_plan_selected' : 'family_plan_selected',
               stepName: '',
               offeravailed: '',
               clicktext: currentPlanContext?.planName,
               journey: 'newpostpaid',
               productcategory: 'postpaid',
               pageName: "PRIORITY_POSTPAID_LANDING_PAGE",
               loggedInStatus: !!loggedInNumber ? 'loggedin' : 'loggedout'
            },
         ],
         utilName: 'handleGtmDataLayerEvent',
      },
      type: 'callUtilFunction',
   })

   return onUserAction({
      actions
   });
}

export const redirectToP2PPage = ({ args, onUserAction, actionContextData, getState }: any) => {
   const actions: any[] = []
   const { storeValues } = getState();
   const { currentPlanContext } = storeValues
   let currentPrice = currentPlanContext?.planPrice || ""
   const type = args[0].type
   const variant = args[0].variant || ""
   const BASE_HREF = getBaseHref();
   let navigationUrl = `${window.location.origin}${BASE_HREF}/prepaid-to-postpaid`
   const isPriorityPostpaidVariant = variant === VARIANT_CONSTANTS.PRIORITY_POSTPAID || variant === VARIANT_CONSTANTS.BUNDLING_PRIORITY_POSTPAID
   if(isPriorityPostpaidVariant){
      navigationUrl += `?utm_layout=p2p_bundling_otp_shift&utm_variant=${VARIANT_CONSTANTS.BUNDLING_PRIORITY_POSTPAID}`
   }
   if (type === "withPlanPrice") {
      navigationUrl += `${isPriorityPostpaidVariant ? '&' : '?'}price=${currentPrice}`
   }
   actions.push(
      {
         type: 'navigation',
         payload: { externalUrl: navigationUrl, shouldAddSearchParams: true },
      }
   )
   return onUserAction({
      actions
   });
}
export const callOtpShiftLoaderActions = (args: any, onUserAction: any, getState: any) => {
   const { storeValues } = getState()
   const cartId = storeValues?.cartContext?.cartId
   const retryCount = storeValues?.user?.retryCount || 1

   const actions = [];
   if (retryCount <= 2) {
      actions.push({
         "analyticsAction": "impression",
         "analyticsData": {
            "eVar33": "{{storeValues:userData.savedAddress.postCode}}",
            "eVar21": "{{storeValues:appData.msisdn}}",
            "eVar42": "{{storeValues:userData.userStatus}}",
            "eVar20": "{{storeValues:user.loggedInNumber}}",
            "eventCategory": "OTP_SHIFT_LOADER_PAGE",
            "eVar36": "{{storeValues:appData.alternateNumber}}",
            "eVar25": "{{storeValues:userData.savedAddress.circleId}}",
            "cartId": "{{storeValues:cartContext.cartItemId}}",
            "section": "Loader_Section",
            "eVar17": "{{storeValues:userData.userInfo}}",
            "eventLabel": "upgrade in progress",
            "eventAction": "impression",
            "prop5": "{{storeValues:selectedPlanContext.planPrice}}",
            "eVar0": "p2p_safo upgrade in progress - loader",
            "subSection": "LoaderWidgetOtpShift",
            "customLabel": "card",
            "eVar41": "{{storeValues:userData.flow}}",
            "category": "{{sessionStorage:intentSelected.type}}",
            "ticketId": "{{storeValues:cartContext.cartId}}",
            "email": "{{storeValues:appData.emailAddress}}"
         }
      }
      )
      actions.push({
         "payload": {
            "authTokenGroup": "FAMILY_PARENT",
            "requestType": "POST",
            "apiUrl": "guardian/api/buy/postpaid/prepaid-to-postpaid/checkoutInitiatorp2p/v1",
            "queryParams": {
               "orderId": "{{additionalData:queryParams.orderId}}"
            },
            "transactUserGroup": "",
            "header": {},
            "body": {
               "createOrder": "{{storeValues:appData.createOrder}}",
               "hvCheckToBePassed": "{{storeValues:userData.hvCheckToBePassed}}",
               "userStatus": "{{storeValues:userData.userStatus}}",
               "addressToPatch": "instlAddress",
               "variant": "{{storeValues:variantContext.variant}}",
               "eliteEligible": "{{storeValues:appData.isEliteEligible}}",
               "updateCartRequest": {
                  "address": "{{storeValues:userData.savedAddress}}",
                  "feasibilityQueryId": "{{storeValues:userData.feasibilityQueryId}}",
                  "cartId": "{{storeValues:cartContext.cartId}}",
                  "planId": "{{storeValues:selectedPlanContext.planId}}",
                  "securityDeposit": "{{storeValues:paymentContext.securityDeposit}}",
                  "rtn": "{{storeValues:appData.alternateNumber}}",
                  "email": "{{storeValues:appData.emailAddress}}",
                  "cartItemId": "{{storeValues:cartContext.cartItemId}}"
               },
               "siNumber": "{{storeValues:appData.msisdn}}",
               "offerApplied": "{{storeValues:appData.isBundlingApplied}}"
            }
         },
         "type": "apiCall"
      })
      actions.push({
         type: 'storeValuesInRedux',
         payload: {
            context: 'user',
            data: {
               "retryCount": retryCount + 1
            }
         }
      })
   } else {
      actions.push({
         "analyticsAction": "impression",
         "analyticsData": {
            "eVar33": "{{storeValues:userData.savedAddress.postCode}}",
            "eVar21": "{{storeValues:appData.msisdn}}",
            "eVar42": "{{storeValues:userData.userStatus}}",
            "eVar20": "{{storeValues:user.loggedInNumber}}",
            "eventCategory": "OTP_SHIFT_LOADER_PAGE",
            "eVar36": "{{storeValues:appData.alternateNumber}}",
            "eVar25": "{{storeValues:userData.savedAddress.circleId}}",
            "cartId": "{{storeValues:cartContext.cartItemId}}",
            "section": "Loader_Section",
            "eVar17": "{{storeValues:userData.userInfo}}",
            "eventLabel": "crossed retry count",
            "eventAction": "impression",
            "prop5": "{{storeValues:selectedPlanContext.planPrice}}",
            "eVar0": "p2p_safo exceeded retry count click - loader",
            "subSection": "LoaderWidgetOtpShift",
            "customLabel": "card",
            "eVar41": "{{storeValues:userData.flow}}",
            "category": "{{sessionStorage:intentSelected.type}}",
            "ticketId": "{{storeValues:cartContext.cartId}}",
            "email": "{{storeValues:appData.emailAddress}}"
         }
      })
   }
   return onUserAction({ actions });

};

export const checkAuthFlow = (onUserAction: any) => {
   const actions: any[] = []
   let isAuthFlow = false;
   let isAppFlow = isMobileApp();


   if (localStorage.getItem('dynamic-token')) {
      isAuthFlow = true;

      const getData = (() => {
         return {
            dynamicToken: localStorage.getItem('dynamic-token'),
            uid: localStorage.getItem('uid'),
            staticToken: localStorage.getItem('static-token'),
            loggedInUser: localStorage.getItem('aa-auk')
         };
      })();

      sessionStorage.setItem('BUY_P2P_WEB', JSON.stringify(getData));

   }

   actions.push({
      type: 'storeValuesInRedux',
      payload: {
         context: 'appData',
         data: {
            isAuthFlow,
            isAppFlow
         },
      },
   });

   return onUserAction({
      actions
   });


};

